<template>
  <div class="password-reset-page">
    <v-container class="py-8">
      <v-row justify="center">
        <v-col cols="12" md="8" lg="6">
          <!-- Logo and Brand Header -->
          <div class="brand-header mb-6">
            <router-link to="/">
              <PassingOnLogo class="brand-logo" tone="light" />
              <h2 class="brand-title text-h5">Passing On</h2>
            </router-link>
          </div>

          <!-- Reset Password Form -->
          <v-card v-if="currentStep === 'reset'" class="main-card" elevation="0">
            <v-card-title class="text-h4 text-center pa-6 primary--text">
              Reset Password
            </v-card-title>

            <v-card-text class="pa-6">
              <div class="mb-6">
                <p class="text-body-1 text--secondary mb-6 text-center">
                  Enter your new password below.
                </p>
              </div>

              <v-alert v-if="error" type="error" variant="tonal" class="mb-4">
                {{ error }}
              </v-alert>

              <v-form ref="resetForm" v-model="formValid" @submit.prevent="handleResetPassword">
                <v-text-field
                  v-model="email"
                  label="Email address"
                  type="email"
                  variant="outlined"
                  density="comfortable"
                  :rules="emailRules"
                  :disabled="loading"
                  required
                  class="mb-4"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  label="New Password"
                  :type="showPassword ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  :rules="passwordRules"
                  :disabled="loading"
                  required
                  :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showPassword = !showPassword"
                  class="mb-4"
                ></v-text-field>

                <v-text-field
                  v-model="passwordConfirm"
                  label="Confirm New Password"
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  variant="outlined"
                  density="comfortable"
                  :rules="confirmPasswordRules"
                  :disabled="loading"
                  required
                  :append-inner-icon="showPasswordConfirm ? 'mdi-eye' : 'mdi-eye-off'"
                  @click:append-inner="showPasswordConfirm = !showPasswordConfirm"
                  class="mb-4"
                ></v-text-field>

                <v-btn
                  type="submit"
                  color="primary"
                  size="large"
                  rounded="0"
                  elevation="0"
                  :disabled="!formValid || loading"
                  :loading="loading"
                  block
                  class="mb-4"
                >
                  Reset Password
                </v-btn>
              </v-form>

              <div class="text-center">
                <v-btn
                  variant="text"
                  color="primary"
                  :disabled="loading"
                  @click="goToLogin"
                >
                  <v-icon left>mdi-arrow-left</v-icon>
                  Back to Sign In
                </v-btn>
              </div>
            </v-card-text>
          </v-card>

          <!-- Success State -->
          <v-card v-else-if="currentStep === 'success'" class="main-card" elevation="0">
            <v-card-text class="text-center pa-8">
              <v-icon size="64" color="success" class="mb-4">mdi-check-circle</v-icon>
              <h3 class="text-h5 mb-2">Password Reset Successful!</h3>
              <p class="text-body-1 text--secondary mb-6">
                Your password has been updated. You can now sign in with your new password.
              </p>
              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                @click="goToLogin"
              >
                Sign In
              </v-btn>
            </v-card-text>
          </v-card>

          <!-- Error/Expired State -->
          <v-card v-else-if="currentStep === 'error'" class="main-card" elevation="0">
            <v-card-text class="text-center pa-8">
              <v-icon size="64" color="error" class="mb-4">mdi-alert-circle</v-icon>
              <h3 class="text-h5 mb-2">Reset Link Invalid</h3>
              <p class="text-body-1 text--secondary mb-6">
                {{ errorMessage || 'This password reset link is invalid or has expired. Please request a new one.' }}
              </p>
              <v-btn
                color="primary"
                size="large"
                rounded="0"
                elevation="0"
                @click="goToLogin"
              >
                Back to Sign In
              </v-btn>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import apiService from '../services/api'

const router = useRouter()
const route = useRoute()

// Form refs
const resetForm = ref(null)

// Form validation
const formValid = ref(false)

// Form data
const email = ref('')
const password = ref('')
const passwordConfirm = ref('')

// UI state
const loading = ref(false)
const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const error = ref('')
const errorMessage = ref('')
const currentStep = ref('reset') // 'reset', 'success', 'error'

// Reset code from URL
const resetCode = ref('')

// Validation rules
const emailRules = [
  v => !!v || 'Email is required',
  v => /.+@.+\..+/.test(v) || 'Email must be valid'
]

const passwordRules = [
  v => !!v || 'Password is required',
  v => (v && v.length >= 5) || 'Password must be at least 5 characters'
]

const confirmPasswordRules = [
  v => !!v || 'Please confirm your password',
  v => v === password.value || 'Passwords do not match'
]

// Methods
const handleResetPassword = async () => {
  if (!formValid.value) return

  loading.value = true
  error.value = ''

  try {
    const response = await apiService.resetPassword(
      email.value,
      resetCode.value,
      password.value,
      passwordConfirm.value
    )

    if (response.success) {
      currentStep.value = 'success'
    } else {
      error.value = response.msg || 'Failed to reset password. Please try again.'
    }
  } catch (err) {
    console.error('Password reset error:', err)
    const errorInfo = apiService.formatErrorMessage(err)
    error.value = errorInfo.message
  } finally {
    loading.value = false
  }
}

const goToLogin = () => {
  router.push('/login')
}

onMounted(() => {
  // Get the reset code from URL params
  resetCode.value = route.params.code || ''

  if (!resetCode.value) {
    currentStep.value = 'error'
    errorMessage.value = 'No reset code provided. Please use the link from your email.'
  }
})
</script>

<style lang="scss" scoped>
@use "~@/styles/variables" as *;
@use "~@/styles/mixins" as *;

.password-reset-page {
  @include page-container;
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(180deg, $primary 0%, rgba(248, 250, 252, 1) 100%);
}

.brand-header {
  text-align: center;

  a {
    display: inline-flex;
    align-items: center;
    gap: 12px;
    text-decoration: none;
  }
}

.brand-logo {
  height: 50px;
  width: auto;
}

.brand-title {
  letter-spacing: 0.1rem;
  font-weight: 600;
  color: white;
  margin: 0;
}

.main-card {
  @include main-card;

  .v-card-title {
    @include section-header;
  }
}

@include mobile {
  .pa-6 {
    padding: 1rem !important;
  }
}
</style>
